import { useCompany } from "../../utils/hooks/useCompany";
import { useFeatureFlags } from "../../utils/hooks/useFeatureFlags";
import { CompanySelectorProps } from "./CompanySelector.types";
import {
  SelectorWrapper,
  SelectorContainer,
  CompanyLogo,
  CompanyName,
  DropdownIcon,
  SelectorDropdown
} from "./CompanySelector.StyledComponents";
import { getCompanyConfig } from "../../types/companies";

export const CompanySelector: React.FC<CompanySelectorProps> = ({ className }) => {
  const { EnableCompanySelector } = useFeatureFlags();
  const {
    selectedCompanyId,
    selectedCompany,
    companies,
    isLoading,
    selectCompany,
  } = useCompany();

  // Gated behind LaunchDarkly flag (FACOPS-2531). When off, behaves as before the
  // FACOPS-892 change effectively disabled the selector.
  if (!EnableCompanySelector) {
    return null;
  }

  // Hide if only one company (auto-selected)
  if (companies.length <= 1) {
    return null;
  }

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const companyId = Number(event.target.value);
    selectCompany(companyId);
  };

  // Get selected company details
  if (selectedCompanyId == null || !selectedCompany) {
    return null;
  }

  // Names come from the backend; logo/brand colors stay frontend assets keyed by id.
  const selectedConfig = getCompanyConfig(selectedCompanyId);

  return (
    <SelectorWrapper className={className}>
      <SelectorContainer
        backgroundColor={selectedConfig.backgroundColor}
        foregroundColor={selectedConfig.foregroundColor}
      >
        <>
          <CompanyLogo
            src={selectedConfig.iconSrc}
            alt={selectedCompany.name}
          />
          <CompanyName>{selectedCompany.name}</CompanyName>
            <DropdownIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </DropdownIcon>
        </>
        <SelectorDropdown
          id="company-selector"
          value={selectedCompanyId ?? ''}
          onChange={handleChange}
          disabled={isLoading}
          aria-label="Select company"
        >
          {companies.map((company) => (
            <option key={company.companyId} value={company.companyId}>
              {company.name}
            </option>
          ))}
        </SelectorDropdown>
      </SelectorContainer>
    </SelectorWrapper>
  );
};